import { useMemo } from 'react';
import { useWorkOrderRunsQuery } from '@api/bff/runs/useRuns';
import { useWorkOrderDetailData } from '../../WorkOrderProfilePage/hooks';
import { formatPositionLabel } from '../../WorkOrderProfilePage/components/VoltmeterRack/utils';
import type {
  WorkOrderRunCellRef,
  WorkOrderRunDetail
} from '@api/bff/runs/useRuns';
import type { WorkOrderDetailViewModel } from '@api/enterprise/openapi/models';

export type InstrumentRunState =
  | 'never'
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'aborted';

export type RunInstrument = {
  woDetailGlobalId: string;
  sequenceNumber: number | null;
  cell: string | null;
  equipmentId: string;
  companyName: string | null;
  description: string | null;
  runState: InstrumentRunState;
  passFail: string | null;
  measurementCount: number;
  dcRuntimeId: string | null;
  inLatestRun: boolean;
};

export type RunInstrumentsRollup = {
  total: number;
  completed: number;
  failed: number;
  neverRun: number;
  latestRunStatus: string | null;
  latestRunCompletedAt: string | null;
};

type PerEquipmentBucket = {
  cell: string | null;
  equipmentGlobalId: string | null;
  measurements: { testPoint: string }[] | null;
};

const parseBuckets = (run: WorkOrderRunDetail | null): PerEquipmentBucket[] => {
  if (!run?.measurementResults) return [];
  try {
    const parsed = JSON.parse(run.measurementResults);
    if (parsed.perEquipment && Array.isArray(parsed.perEquipment)) {
      return parsed.perEquipment as PerEquipmentBucket[];
    }
    return [];
  } catch {
    return [];
  }
};

const mapRunState = (status: string | undefined): InstrumentRunState => {
  switch (status?.toUpperCase()) {
    case 'PENDING':
      return 'pending';
    case 'RUNNING':
      return 'running';
    case 'COMPLETED':
      return 'completed';
    case 'FAILED':
      return 'failed';
    case 'ABORTED':
      return 'aborted';
    default:
      return 'never';
  }
};

const detailPositionLabel = (
  detail: WorkOrderDetailViewModel
): string | null =>
  detail.sequenceNumber != null
    ? formatPositionLabel(detail.sequenceNumber)
    : null;

export const useRunInstruments = (jobNumber: string) => {
  const {
    details,
    workOrder,
    isLoading: isDetailsLoading,
    error: detailsError
  } = useWorkOrderDetailData({ jobNumber });

  const runsQuery = useWorkOrderRunsQuery(jobNumber, 1, 1);
  const latestRun = runsQuery.data?.data?.[0] ?? null;

  const { instruments, rollup } = useMemo(() => {
    const buckets = parseBuckets(latestRun);
    const runCells = latestRun?.cells ?? [];
    const runState = mapRunState(latestRun?.status);

    const findDetailForCell = (
      cellLabel: string,
      equipmentRef: WorkOrderRunCellRef['equipmentRef']
    ): WorkOrderDetailViewModel | undefined => {
      const candidateIds = [equipmentRef?.id, equipmentRef?.globalId]
        .map(value => (value ?? '').trim())
        .filter(value => value !== '');
      const byEquipment =
        candidateIds.length > 0
          ? details.find(d => candidateIds.includes((d.id ?? '').trim()))
          : undefined;
      return (
        byEquipment ?? details.find(d => detailPositionLabel(d) === cellLabel)
      );
    };

    const findBucket = (
      cellLabel: string | null,
      equipmentId: string
    ): PerEquipmentBucket | undefined =>
      buckets.find(
        b =>
          (b.equipmentGlobalId != null &&
            b.equipmentGlobalId.trim() === equipmentId.trim()) ||
          (cellLabel != null && b.cell === cellLabel)
      );

    const toInstrument = (
      detail: WorkOrderDetailViewModel,
      cellLabel: string | null,
      inLatestRun: boolean,
      cellDcRuntimeId: string | null = null
    ): RunInstrument => {
      const equipmentId = (detail.id ?? '').trim();
      const bucket = inLatestRun
        ? findBucket(cellLabel, equipmentId)
        : undefined;
      return {
        woDetailGlobalId: detail.globalId ?? '',
        sequenceNumber: detail.sequenceNumber ?? null,
        cell: cellLabel ?? detailPositionLabel(detail),
        equipmentId,
        companyName: detail.companyName ?? null,
        description: detail.description ?? null,
        runState: inLatestRun ? runState : 'never',
        passFail: inLatestRun ? latestRun?.overallResult ?? null : null,
        measurementCount: bucket?.measurements?.length ?? 0,
        dcRuntimeId: inLatestRun
          ? cellDcRuntimeId ?? latestRun?.dcRuntimeId ?? null
          : null,
        inLatestRun
      };
    };

    const seen = new Set<string>();
    const ordered: RunInstrument[] = [];

    for (const cell of runCells) {
      const detail = findDetailForCell(cell.cell, cell.equipmentRef);
      if (!detail?.globalId || seen.has(detail.globalId)) continue;
      seen.add(detail.globalId);
      ordered.push(
        toInstrument(detail, cell.cell, true, cell.dcRuntimeId ?? null)
      );
    }

    const remaining = details
      .filter(d => d.globalId && !seen.has(d.globalId))
      .sort((a, b) => (a.sequenceNumber ?? 0) - (b.sequenceNumber ?? 0));
    for (const detail of remaining) {
      ordered.push(toInstrument(detail, null, false));
    }

    const summary: RunInstrumentsRollup = {
      total: ordered.length,
      completed: ordered.filter(i => i.runState === 'completed').length,
      failed: ordered.filter(
        i => i.runState === 'failed' || i.runState === 'aborted'
      ).length,
      neverRun: ordered.filter(i => i.runState === 'never').length,
      latestRunStatus: latestRun?.status ?? null,
      latestRunCompletedAt: latestRun?.completedAt ?? null
    };

    return { instruments: ordered, rollup: summary };
  }, [details, latestRun]);

  return {
    instruments,
    rollup,
    workOrder,
    latestRun,
    isLoading: isDetailsLoading || runsQuery.isLoading,
    error: detailsError || runsQuery.error?.message || null
  };
};
